import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { getCanonicalRoles, roleLabelsFor } from "../lib/canonicalRoles";
import {
  UserCircle, CheckCircle, XCircle, SpinnerGap, ArrowClockwise,
  Clock, Buildings, Envelope, ShieldCheck
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import SortDropdown, { sortItems } from "../components/common/SortDropdown";

import { API_BASE as API } from "../lib/apiBase";

// Vincent 2026-06-03 (Eric) — Liste canonique unique des rôles (Gestion des
// rôles). 8 options exactement : Visiteur · Employé · Nouvel employé · Employé
// formé · Employé avancé · Concepteur · Admin · Fondateur. Plus de president/
// ceo/vp séparés (absorbés par Admin). Les libellés peuvent être personnalisés
// par org via roleLabelsFor(user, lang).

export default function UserApprovalPage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  // Vincent 2026-06-03 — Options canoniques dérivées de roleLabelsFor(user, lang).
  const ROLE_OPTIONS = useMemo(
    () => getCanonicalRoles(user, lang).map((r) => ({ value: r.id, label: r.label })),
    [user]
  );
  const [pending, setPending] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [isFounder, setIsFounder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(null);

  // Décisions d'approbation par user.id (rôle / 2e entreprise / org override)
  // Structure: { [userId]: { org_role, secondary_org_id, has_secondary, org_id_override } }
  const [decisions, setDecisions] = useState({});

  // Vincent #332 (Eric 2026-06-17) — Tri des demandes d'accès
  const [pendingSortBy, setPendingSortBy] = useState("alpha_asc");
  const sortedPending = useMemo(() => sortItems(pending, pendingSortBy), [pending, pendingSortBy]);

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/platform/pending-users`, { withCredentials: true });
      setPending(data.pending || []);
      setPendingInvites(data.pending_invites || []);
      setRejected(data.rejected || []);
      setOrgs(data.organizations || []);
      setIsFounder(!!data.is_founder);
      // Initialise décisions par défaut : titre = employee_trained (conseiller)
      const seed = {};
      (data.pending || []).forEach((u) => {
        seed[u.id] = {
          org_role: "employee_trained",
          has_secondary: false,
          secondary_org_id: "",
          org_id_override: u.org_id || "",
        };
      });
      setDecisions(seed);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    }
    setLoading(false);
  };

  const updateDecision = (userId, patch) => {
    setDecisions((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), ...patch } }));
  };

  const approveUser = async (targetUser) => {
    const d = decisions[targetUser.id] || {};
    setProcessing(targetUser.id);
    try {
      const payload = {
        org_role: d.org_role || "employee_trained",
      };
      // Le founder peut overrider l'org cible
      if (isFounder && d.org_id_override && d.org_id_override !== targetUser.org_id) {
        payload.org_id = d.org_id_override;
      }
      if (d.has_secondary && d.secondary_org_id) {
        payload.secondary_org_id = d.secondary_org_id;
      }
      await axios.post(`${API}/api/platform/approve-user/${targetUser.id}`, payload, { withCredentials: true });
      const orgLabel = (orgs.find((o) => o.id === (payload.org_id || targetUser.org_id)) || {}).name || "entreprise inconnue";
      const titleLabel = (ROLE_OPTIONS.find((r) => r.value === payload.org_role) || {}).label || payload.org_role;
      toast.success(`${targetUser.name || targetUser.email} approuvé : ${titleLabel} chez ${orgLabel}`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'approbation");
    }
    setProcessing(null);
  };

  const rejectUser = async (userId) => {
    setProcessing(userId);
    try {
      await axios.post(`${API}/api/platform/reject-user/${userId}`, { reason: rejectReason }, { withCredentials: true });
      setShowRejectModal(null);
      setRejectReason("");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du refus");
    }
    setProcessing(null);
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={32} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-approval-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/20 flex items-center justify-center">
            <ShieldCheck size={24} className="text-amber-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-['Outfit']" data-testid="approval-title">{lang === "en" ? "Access Requests" : "Demandes d'accès"}</h1>
            <p className="text-xs text-muted-foreground">
              {pending.length} {lang === "en" ? "pending requests" : "demande(s) en attente"}
              {!isFounder && " — votre entreprise"}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} className="gap-1.5" data-testid="approval-refresh-btn"><ArrowClockwise size={14} /> {t("refresh")}</Button>
      </div>

      {/* Eric iter83b — Bouton "Ménage" (Vincent demande urgente) */}
      <div className="bg-card border border-border rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="cleanup-section">
        <div className="text-sm">
          <p className="font-medium">{lang === "fr" ? "Ménage des invitations résiduelles" : "Cleanup stale invitations"}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {lang === "fr"
              ? "Purge les invitations orphelines (compte supprimé ou jamais cliquées) et les demandes pendantes anciennes (>7 jours) ou déjà refusées."
              : "Purge orphan invitations and stale pending requests (>7 days) or already rejected."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm"
            data-testid="cleanup-preview-btn"
            onClick={async () => {
              try {
                const { data } = await axios.post(`${API}/api/platform/cleanup-stale-invites`, { dry_run: true, older_than_days: 7 }, { withCredentials: true });
                toast.message(
                  lang === "fr" ? "Aperçu du ménage" : "Cleanup preview",
                  { description:
                      `${data.orphan_pending_invites} invites pending orphelines · ${data.orphan_accepted_invites} accepted orphelines · ` +
                      `${(data.stale_pending_users || []).length} pending users anciens · ${(data.rejected_users || []).length} rejected à purger.`,
                    duration: 8000,
                  }
                );
              } catch (e) {
                toast.error(e.response?.data?.detail || "Erreur");
              }
            }}
          >
            {lang === "fr" ? "Prévisualiser" : "Preview"}
          </Button>
          <Button
            variant="destructive" size="sm"
            data-testid="cleanup-execute-btn"
            onClick={async () => {
              const ok = await confirmDialog(
                lang === "fr"
                  ? "Confirmer la purge ? Les invitations résiduelles et demandes anciennes seront supprimées définitivement."
                  : "Confirm purge? Stale invites and old requests will be deleted permanently."
              );
              if (!ok) return;
              try {
                const { data } = await axios.post(`${API}/api/platform/cleanup-stale-invites`, { dry_run: false, older_than_days: 7 }, { withCredentials: true });
                const d = data.deleted || {};
                toast.success(
                  (lang === "fr" ? "Ménage terminé : " : "Cleanup done: ") +
                  `${d.pending_invites || 0} pending invites · ${d.accepted_invites || 0} accepted invites · ` +
                  `${d.pending_users || 0} pending users · ${d.rejected_users || 0} rejected users supprimés.`
                );
                await loadData();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Erreur");
              }
            }}
          >
            {lang === "fr" ? "Exécuter le ménage" : "Run cleanup"}
          </Button>
        </div>
      </div>

      {/* Pending Invites — Vincent #218 (Eric 2026-06-04) — Liste des invitations
          envoyées en attente d'inscription par le destinataire. */}
      {pendingInvites.length > 0 && (
        <div className="bg-card border border-blue-500/20 rounded-xl p-5" data-testid="pending-invites-section">
          <h2 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <Envelope size={16} className="text-blue-500" />
            {lang === "fr" ? `Invitations en attente (${pendingInvites.length})` : `Pending invitations (${pendingInvites.length})`}
          </h2>
          <div className="space-y-2" data-testid="pending-invites-list">
            {pendingInvites.map(inv => (
              <div key={inv.id || inv.token} className="flex items-center justify-between gap-3 py-2 border-b border-border/30 last:border-0" data-testid={`pending-invite-${inv.email}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {inv.name || inv.email}
                    <span className="ml-2 inline-block px-2 py-0.5 text-[10px] rounded bg-blue-500/15 text-blue-600 font-semibold uppercase tracking-wide">
                      {lang === "fr" ? "Invité" : "Invited"}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {inv.email} · {inv.org_name || inv.org_id} · {lang === "fr" ? "par" : "by"} {inv.invited_by_name || "—"}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs text-muted-foreground whitespace-nowrap">
                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA") : ""}
                  </div>
                  {/* Vincent #268 (2026-02-06) — Bouton effacer une invitation
                      en attente, avec confirmation. Endpoint DELETE
                      /api/organizations/invites/{id} déjà existant. */}
                  <button
                    onClick={async () => {
                      const ok = await confirmDialog(
                        lang === "fr"
                          ? `Effacer l'invitation envoyée à ${inv.email} ? Le lien d'invitation ne fonctionnera plus.`
                          : `Cancel the invitation for ${inv.email}? The invite link will stop working.`
                      );
                      if (!ok) return;
                      try {
                        await axios.delete(`${API}/api/organizations/invites/${inv.id}`, { withCredentials: true });
                        toast.success(lang === "fr" ? "Invitation effacée" : "Invitation cancelled");
                        loadData();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Erreur");
                      }
                    }}
                    className="p-1.5 hover:bg-rose-50 rounded-md text-rose-600 transition-colors"
                    title={lang === "fr" ? "Effacer cette invitation" : "Cancel invitation"}
                    data-testid={`cancel-invite-${inv.email}`}
                  >
                    <XCircle size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending */}
      {pending.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border rounded-xl">
          <CheckCircle size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">{lang === "en" ? "No pending requests" : "Aucune demande en attente"}</p>
          <p className="text-sm mt-1">{lang === "en" ? "All access requests have been processed" : "Toutes les demandés ont ete traitées"}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="pending-list">
          {/* Vincent #332 (Eric 2026-06-17) — Dropdown de tri des demandes */}
          <div className="flex justify-end">
            <SortDropdown value={pendingSortBy} onChange={setPendingSortBy} testId="pending-sort" />
          </div>
          {sortedPending.map(u => {
            const d = decisions[u.id] || { org_role: "employee_trained", has_secondary: false, secondary_org_id: "", org_id_override: u.org_id || "" };
            const targetOrg = orgs.find((o) => o.id === (d.org_id_override || u.org_id));
            return (
              <div key={u.id || u.email} className="bg-card border border-amber-500/20 rounded-xl p-5" data-testid={`pending-user-${u.email}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-4 min-w-[260px]">
                    <div className="w-11 h-11 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
                      <UserCircle size={24} className="text-amber-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{u.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Envelope size={12} /> {u.email}
                      </p>
                      {u.company_name && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                          <Buildings size={12} /> {u.company_name} <span className="text-muted-foreground/50">(saisi par l'utilisateur)</span>
                        </p>
                      )}
                      {targetOrg && (
                        <p className="text-xs text-teal-700 flex items-center gap-1.5 mt-0.5">
                          <Buildings size={12} weight="fill" /> Sera affecté à : <strong>{targetOrg.name}</strong>
                        </p>
                      )}
                      {u.registration_reason && (
                        <p className="text-xs text-primary/60 mt-1 italic">"{u.registration_reason}"</p>
                      )}
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-1">
                        <Clock size={10} /> {new Date(u.created_at).toLocaleString("fr-FR")}
                        {u.partner_code && <span className="ml-2 px-1.5 py-0.5 bg-secondary rounded text-[9px]">Code: {u.partner_code}</span>}
                        {u.signup_coupon_code && <span className="ml-1 px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded text-[9px]">Coupon: {u.signup_coupon_code}</span>}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Vincent #193 — Configuration de l'assignation */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-secondary/30 rounded-lg border border-border" data-testid={`assignment-config-${u.email}`}>
                  {/* Entreprise principale (founder only) */}
                  {isFounder && (
                    <div>
                      <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Entreprise principale</label>
                      <select
                        value={d.org_id_override || ""}
                        onChange={(e) => updateDecision(u.id, { org_id_override: e.target.value })}
                        className="w-full text-sm px-2 py-1.5 bg-background border border-border rounded"
                        data-testid={`org-select-${u.email}`}
                      >
                        <option value="">— Choisir une entreprise —</option>
                        {orgs.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* Titre / poste */}
                  <div>
                    <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Titre / poste</label>
                    <select
                      value={d.org_role}
                      onChange={(e) => updateDecision(u.id, { org_role: e.target.value })}
                      className="w-full text-sm px-2 py-1.5 bg-background border border-border rounded"
                      data-testid={`role-select-${u.email}`}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Vincent #193 — 2e entreprise (case à cocher + dropdown) */}
                <div className="mt-2 flex flex-col gap-2 p-3 bg-secondary/20 rounded-lg" data-testid={`secondary-org-block-${u.email}`}>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={d.has_secondary}
                      onChange={(e) => updateDecision(u.id, { has_secondary: e.target.checked, secondary_org_id: e.target.checked ? d.secondary_org_id : "" })}
                      className="w-4 h-4"
                      data-testid={`secondary-toggle-${u.email}`}
                    />
                    Ajouter aussi à une 2e entreprise
                  </label>
                  {d.has_secondary && (
                    <select
                      value={d.secondary_org_id}
                      onChange={(e) => updateDecision(u.id, { secondary_org_id: e.target.value })}
                      className="w-full text-sm px-2 py-1.5 bg-background border border-border rounded"
                      data-testid={`secondary-org-select-${u.email}`}
                    >
                      <option value="">— Choisir l'entreprise secondaire —</option>
                      {orgs.filter((o) => o.id !== (d.org_id_override || u.org_id)).map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="mt-3 flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => { setShowRejectModal(u); setRejectReason(""); }}
                    disabled={processing === u.id}
                    className="gap-1.5"
                    data-testid={`reject-${u.email}`}
                  >
                    <XCircle size={14} /> {t("reject")}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => approveUser(u)}
                    disabled={processing === u.id || (isFounder && !(d.org_id_override))}
                    className="gap-1.5 bg-teal-600 hover:bg-teal-500"
                    data-testid={`approve-${u.email}`}
                  >
                    {processing === u.id ? <SpinnerGap size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    {t("approve")} & assigner
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Rejected */}
      {rejected.length > 0 && (
        <div data-testid="rejected-list">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            {lang === "en" ? "Rejected" : "Refuses"} ({rejected.length})
          </h2>
          <div className="space-y-2">
            {rejected.map(u => (
              <div key={u.id || u.email} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between opacity-60" data-testid={`rejected-user-${u.email}`}>
                <div className="flex items-center gap-3">
                  <XCircle size={20} className="text-rose-400" />
                  <div>
                    <p className="text-sm font-medium">{u.name} — <span className="text-muted-foreground">{u.email}</span></p>
                    {u.rejection_reason && <p className="text-xs text-rose-400 mt-0.5">Raison: {u.rejection_reason}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRejectModal(null)}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="reject-modal">
            <h3 className="font-bold text-lg mb-2">{lang === "en" ? "Reject access" : "Refuser l'accès"}</h3>
            <p className="text-sm text-muted-foreground mb-4">{showRejectModal.name} ({showRejectModal.email})</p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder={lang === "en" ? "Reason for rejection (optional)..." : "Raison du refus (optionnel)..."}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm resize-none mb-4"
              data-testid="reject-reason-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowRejectModal(null)} className="flex-1">{t("cancel")}</Button>
              <Button variant="destructive" onClick={() => rejectUser(showRejectModal.id)} disabled={processing === showRejectModal.id} className="flex-1 gap-1.5" data-testid="confirm-reject-btn">
                {processing === showRejectModal.id ? <SpinnerGap size={14} className="animate-spin" /> : <XCircle size={14} />}
                {lang === "en" ? "Confirm rejection" : "Confirmer le refus"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
